import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from "@/api/axios";
import { Home, Car, Smartphone, Fuel, TreePine, Droplets, Truck } from 'lucide-react';
import { useTranslation } from "react-i18next";
import { InfoTooltip } from './Infotooltip';

export default function EnvironmentalImpactCard({ selectedBins, selectedPeriod, selectedYear, customDateRange }) {
  const [currentItem, setCurrentItem] = useState(null);
  const [co2Data, setCo2Data] = useState({
    total_co2_without_garby: 0,
    total_co2_with_garby: 0,
    co2_savings: 0,
    equivalents: {},
    period_start: null,
    period_end: null
  });
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  const impactItems = [
    { icon: Home,       color: 'text-emerald-500 dark:text-emerald-400', bgColor: 'bg-emerald-50 dark:bg-emerald-900/30', key: 'home_electricity' },
    { icon: Car,        color: 'text-blue-500 dark:text-blue-400',       bgColor: 'bg-blue-50 dark:bg-blue-900/30',       key: 'average_car_drive' },
    { icon: Smartphone, color: 'text-purple-500 dark:text-purple-400',   bgColor: 'bg-purple-50 dark:bg-purple-900/30',   key: 'smartphone_charges' },
    { icon: Fuel,       color: 'text-yellow-500 dark:text-yellow-400',   bgColor: 'bg-yellow-50 dark:bg-yellow-900/30',   key: 'gasoline_consumed' },
    { icon: TreePine,   color: 'text-green-500 dark:text-green-400',     bgColor: 'bg-green-50 dark:bg-green-900/30',     key: 'tree_sequestration' },
    { icon: Droplets,   color: 'text-cyan-500 dark:text-cyan-400',       bgColor: 'bg-cyan-50 dark:bg-cyan-900/30',       key: 'water_bottles' },
    { icon: Truck,      color: 'text-orange-500 dark:text-orange-400',   bgColor: 'bg-orange-50 dark:bg-orange-900/30',   key: 'lorry_drive' }
  ];

  const selectRandomEquivalent = useCallback((equivalents) => {
    const availableItems = impactItems.filter(item =>
      equivalents[item.key] && equivalents[item.key].value > 0
    );
    return availableItems.length > 0
      ? availableItems[Math.floor(Math.random() * availableItems.length)]
      : impactItems[0];
  }, []);

  // Stable key so useEffect fires when the custom range actually changes
  const customDateRangeKey = (() => {
    const r = customDateRange?.[0];
    if (!r?.startDate || !r?.endDate) return null;
    const start = r.startDate instanceof Date ? r.startDate.toISOString() : r.startDate;
    const end   = r.endDate   instanceof Date ? r.endDate.toISOString()   : r.endDate;
    return `${start}__${end}`;
  })();

  const isCustom = Boolean(customDateRangeKey);

  useEffect(() => {
    let isMounted = true;

    const fetchEnvironmentalImpact = async () => {
      try {
        setLoading(true);

        const params = { 'bins[]': selectedBins };

        if (isCustom) {
          const r = customDateRange[0];
          params.start_date = r.startDate instanceof Date ? r.startDate.toISOString() : r.startDate;
          params.end_date   = r.endDate   instanceof Date ? r.endDate.toISOString()   : r.endDate;
        } else {
          params.period = selectedPeriod;
          if (selectedPeriod === 'year') params.year = selectedYear;
        }

        const response = await axiosInstance.get('enviromental_impact', { params });

        if (isMounted) {
          setCo2Data(response.data);
          setCurrentItem(selectRandomEquivalent(response.data.equivalents));
        }
      } catch (error) {
        console.error('Error fetching environmental impact data:', error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchEnvironmentalImpact();
    return () => { isMounted = false; };
  }, [selectedBins, selectedPeriod, selectedYear, customDateRangeKey]);

  const { co2_savings, equivalents, period_start, period_end } = co2Data;
  const currentValue = currentItem && equivalents[currentItem.key]
    ? equivalents[currentItem.key].value
    : 0;

  const formatDate = (dateString) => {
    if (!dateString) return '';
    // FIX: no timeZone option means this renders in the *browser's* local
    // timezone, not the server's. The backend already computes the right
    // date boundary in Europe/Berlin time — letting the browser reinterpret
    // that instant in its own timezone can roll it back a day (e.g. dev
    // machine set to a timezone with a smaller UTC offset than Berlin's
    // CEST). Pin timeZone explicitly, same as the other dashboard cards.
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: (!isCustom && selectedPeriod === 'year') ? 'numeric' : undefined,
      timeZone: 'Europe/Berlin'
    });
  };

  const getPeriodText = () => {
    if (!period_start || !period_end) return '';
    if (isCustom) return `${formatDate(period_start)} - ${formatDate(period_end)}`;
    switch (selectedPeriod) {
      case 'day':    return formatDate(period_start);
      case 'month':  return new Date(period_start).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'Europe/Berlin' });
      case 'year':   return selectedYear.toString();
      default:       return `${formatDate(period_start)} - ${formatDate(period_end)}`;
    }
  };

  const getDisplayText = (item, value) => {
    const formattedValue = item.key === 'home_electricity'
      ? value.toFixed(2)
      : value.toLocaleString();
    return `${formattedValue} ${t(`enviromentImpact.equivalents.${item.key}`)}`;
  };

  return (
    <div className="rounded-lg border bg-card text-card-foreground dark:hover:bg-slate-900 hover:cursor-pointer flex flex-col w-full h-[200px]">
      <div className="px-4 py-3 flex items-center justify-between">
        <h2 className="tracking-tight text-sm font-medium pb-2">{t('enviromentImpact.defaultTitle')}</h2>
        <div className="flex items-center gap-2 pb-2">
          {isCustom && !loading && (
            <span className="text-xs text-muted-foreground">{getPeriodText()}</span>
          )}
          <InfoTooltip text="Estimated CO₂e avoided by correctly sorting waste, compared to sending everything to residual. Contaminated items earn no credit." />
        </div>
      </div>

      {loading ? (
        // Skeleton — number line + icon-and-caption block, same slots the
        // loaded card fills, so the layout doesn't jump on arrival.
        <div className="flex-1 flex flex-col motion-safe:animate-pulse" aria-busy="true">
          <span className="sr-only" role="status">{t('enviromentImpact.fetchingData')}</span>
          <div className="px-4 pb-1">
            <div className="h-6 w-48 rounded bg-gray-200 dark:bg-slate-700" />
          </div>
          <div className="flex-1 flex items-center justify-center px-4">
            <div className="w-full text-center flex flex-col items-center gap-2">
              <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-slate-700" />
              <div className="h-3 w-32 rounded bg-gray-200 dark:bg-slate-700" />
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="px-4 pb-1 text-2xl font-bold text-emerald-600 dark:text-emerald-400">
            {`${co2_savings.toFixed(2)} kg CO₂ ${t('enviromentImpact.co2SavedSuffix')}`}
          </div>

          <div className="flex-1 flex items-center justify-center px-4">
            {currentItem && (
              <div className="w-full text-center">
                <div className={`w-12 h-12 rounded-full ${currentItem.bgColor} flex items-center justify-center mx-auto mb-2`}>
                  <currentItem.icon className={`h-5 w-5 ${currentItem.color}`} />
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {getDisplayText(currentItem, currentValue)}
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}